import { Link } from 'react-router-dom'
import { Button } from '@/components/ui'
import { SEO } from '@/components/seo'

// Journey hub card data - all available journeys
interface JourneyHubCard {
  slug: string
  colorClass: 'meeting' | 'ramble' | 'growth' | 'referral' | 'weekends' | 'headroom' | 'portal' | 'adviser-os'
  icon: 'video' | 'microphone' | 'chart' | 'users' | 'sun' | 'presentation' | 'pound' | 'sparkle'
  title: string
  tagline: string
  description: string
}

const journeyHubCards: JourneyHubCard[] = [
  {
    slug: 'meeting-to-done',
    colorClass: 'meeting',
    icon: 'video',
    title: 'Meeting to Done',
    tagline: 'Client meeting at 10am. Report by lunch.',
    description:
      "Follow a retirement planning meeting as it transforms into transcription, research, and a compliant suitability report - all before you've finished your coffee.",
  },
  {
    slug: 'ramble-to-action',
    colorClass: 'ramble',
    icon: 'microphone',
    title: 'Ramble to Action',
    tagline: 'Speak your thoughts. Watch them happen.',
    description:
      'Dictate into your Apple Watch or CarPlay while driving. By the time you arrive, your thoughts have become tasks, notes, and client follow-ups.',
  },
  {
    slug: 'practice-growth',
    colorClass: 'growth',
    icon: 'chart',
    title: '80 Clients to Exit',
    tagline: 'From lifestyle practice to 7-figure sale.',
    description:
      "The 5-year story of how one adviser doubled their client book, tripled their AUM, and sold for 2.5x what they expected - all by reclaiming 15 hours a week.",
  },
  {
    slug: 'portal',
    colorClass: 'portal',
    icon: 'users',
    title: 'Annual to Always',
    tagline: 'Your client portal, always on.',
    description:
      'Transform the annual review into an ongoing conversation. Clients see their wealth, documents, and progress 24/7. Engagement that builds loyalty.',
  },
  {
    slug: 'reclaim-weekends',
    colorClass: 'weekends',
    icon: 'sun',
    title: 'Reclaim Weekends',
    tagline: 'From Sunday dread to Sunday roast.',
    description:
      'The story of getting your life back. No more weekend catch-up sessions. No more guilt about the backlog. Just time with the people who matter.',
  },
  {
    slug: 'headcount-to-headroom',
    colorClass: 'headroom',
    icon: 'presentation',
    title: 'Headcount to Headroom',
    tagline: 'Scale without the recruitment headache.',
    description:
      'Paraplanners are hard to find and expensive to outsource. See how firms create 40% more capacity without hiring a single person.',
  },
  {
    slug: 'referral-flywheel',
    colorClass: 'referral',
    icon: 'pound',
    title: 'Price to Value',
    tagline: 'Your advice is valuable. Help clients see it.',
    description:
      'Transform MiFID II cost disclosures into value demonstrations. Show clients exactly what they get for their fees with beautiful reports and calculators.',
  },
  {
    slug: 'adviser-os',
    colorClass: 'adviser-os',
    icon: 'sparkle',
    title: 'Adviser OS',
    tagline: 'One platform. Your entire practice.',
    description:
      'Stop juggling 15 different tools. WealthSpace is the operating system that runs your advice firm - reports, meetings, compliance, clients, and growth - all in one place.',
  },
]

// Journey icons
function JourneyIcon({ icon }: { icon: JourneyHubCard['icon'] }) {
  const iconProps = {
    width: 26,
    height: 26,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: '#0a0d12',
    strokeWidth: 2,
  }

  switch (icon) {
    case 'video':
      return (
        <svg {...iconProps}>
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
          />
        </svg>
      )
    case 'microphone':
      return (
        <svg {...iconProps}>
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z"
          />
        </svg>
      )
    case 'chart':
      return (
        <svg {...iconProps}>
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M2.25 18L9 11.25l4.306 4.307a11.95 11.95 0 015.814-5.519l2.74-1.22m0 0l-5.94-2.28m5.94 2.28l-2.28 5.941"
          />
        </svg>
      )
    case 'users':
      return (
        <svg {...iconProps}>
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z"
          />
        </svg>
      )
    case 'sun':
      return (
        <svg {...iconProps}>
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 3v2.25m6.364.386l-1.591 1.591M21 12h-2.25m-.386 6.364l-1.591-1.591M12 18.75V21m-4.773-4.227l-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0z"
          />
        </svg>
      )
    case 'presentation':
      return (
        <svg {...iconProps}>
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M3.75 3v11.25A2.25 2.25 0 006 16.5h2.25M3.75 3h-1.5m1.5 0h16.5m0 0h1.5m-1.5 0v11.25A2.25 2.25 0 0118 16.5h-2.25m-7.5 0h7.5m-7.5 0l-1 3m8.5-3l1 3m0 0l.5 1.5m-.5-1.5h-9.5m0 0l-.5 1.5m.75-9l3-3 2.148 2.148A12.061 12.061 0 0116.5 7.605"
          />
        </svg>
      )
    case 'pound':
      return (
        <svg {...iconProps}>
          <circle cx="12" cy="12" r="9" strokeLinecap="round" strokeLinejoin="round" />
          <text
            x="12"
            y="16"
            textAnchor="middle"
            fontSize="12"
            fontWeight="600"
            fill="#0a0d12"
            stroke="none"
            fontFamily="Arial"
          >
            £
          </text>
        </svg>
      )
    case 'sparkle':
      return (
        <svg {...iconProps}>
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z"
          />
        </svg>
      )
  }
}

// Color classes mapping for journey cards
const colorStyles: Record<
  JourneyHubCard['colorClass'],
  {
    iconBg: string
    beforeBg: string
    afterBg: string
    ctaColor: string
    accentLine: string
  }
> = {
  meeting: {
    iconBg: 'bg-gradient-to-br from-[#14b8a6] to-[#0d9488]',
    beforeBg:
      'before:bg-gradient-to-br before:from-[#14b8a6] before:via-[#0d9488] before:to-[#065f5b]',
    afterBg: 'after:bg-[radial-gradient(ellipse_at_bottom,rgba(20,184,166,0.15)_0%,transparent_60%)]',
    ctaColor: 'text-[#14b8a6]',
    accentLine: 'bg-gradient-to-r from-[#14b8a6] to-[#2dd4bf]',
  },
  ramble: {
    iconBg: 'bg-gradient-to-br from-[#8b5cf6] to-[#7c3aed]',
    beforeBg:
      'before:bg-gradient-to-br before:from-[#8b5cf6] before:via-[#7c3aed] before:to-[#5b21b6]',
    afterBg: 'after:bg-[radial-gradient(ellipse_at_bottom,rgba(139,92,246,0.15)_0%,transparent_60%)]',
    ctaColor: 'text-[#8b5cf6]',
    accentLine: 'bg-gradient-to-r from-[#8b5cf6] to-[#a78bfa]',
  },
  growth: {
    iconBg: 'bg-gradient-to-br from-[#f59e0b] to-[#d97706]',
    beforeBg:
      'before:bg-gradient-to-br before:from-[#f59e0b] before:via-[#d97706] before:to-[#b45309]',
    afterBg: 'after:bg-[radial-gradient(ellipse_at_bottom,rgba(245,158,11,0.15)_0%,transparent_60%)]',
    ctaColor: 'text-[#f59e0b]',
    accentLine: 'bg-gradient-to-r from-[#f59e0b] to-[#fbbf24]',
  },
  referral: {
    iconBg: 'bg-gradient-to-br from-[#ec4899] to-[#db2777]',
    beforeBg:
      'before:bg-gradient-to-br before:from-[#ec4899] before:via-[#db2777] before:to-[#be185d]',
    afterBg: 'after:bg-[radial-gradient(ellipse_at_bottom,rgba(236,72,153,0.15)_0%,transparent_60%)]',
    ctaColor: 'text-[#ec4899]',
    accentLine: 'bg-gradient-to-r from-[#ec4899] to-[#f472b6]',
  },
  weekends: {
    iconBg: 'bg-gradient-to-br from-[#06b6d4] to-[#0891b2]',
    beforeBg:
      'before:bg-gradient-to-br before:from-[#06b6d4] before:via-[#0891b2] before:to-[#0e7490]',
    afterBg: 'after:bg-[radial-gradient(ellipse_at_bottom,rgba(6,182,212,0.15)_0%,transparent_60%)]',
    ctaColor: 'text-[#06b6d4]',
    accentLine: 'bg-gradient-to-r from-[#06b6d4] to-[#22d3ee]',
  },
  headroom: {
    iconBg: 'bg-gradient-to-br from-[#6366f1] to-[#4f46e5]',
    beforeBg:
      'before:bg-gradient-to-br before:from-[#6366f1] before:via-[#4f46e5] before:to-[#4338ca]',
    afterBg: 'after:bg-[radial-gradient(ellipse_at_bottom,rgba(99,102,241,0.15)_0%,transparent_60%)]',
    ctaColor: 'text-[#6366f1]',
    accentLine: 'bg-gradient-to-r from-[#6366f1] to-[#818cf8]',
  },
  portal: {
    iconBg: 'bg-gradient-to-br from-[#10b981] to-[#059669]',
    beforeBg:
      'before:bg-gradient-to-br before:from-[#10b981] before:via-[#059669] before:to-[#047857]',
    afterBg: 'after:bg-[radial-gradient(ellipse_at_bottom,rgba(16,185,129,0.15)_0%,transparent_60%)]',
    ctaColor: 'text-[#10b981]',
    accentLine: 'bg-gradient-to-r from-[#10b981] to-[#34d399]',
  },
  'adviser-os': {
    iconBg: 'bg-gradient-to-br from-[#14b8a6] to-[#0d9488]',
    beforeBg:
      'before:bg-gradient-to-br before:from-[#14b8a6] before:via-[#0d9488] before:to-[#065f5b]',
    afterBg: 'after:bg-[radial-gradient(ellipse_at_bottom,rgba(20,184,166,0.15)_0%,transparent_60%)]',
    ctaColor: 'text-[#14b8a6]',
    accentLine: 'bg-gradient-to-r from-[#14b8a6] to-[#2dd4bf]',
  },
}

function JourneyCardComponent({ journey }: { journey: JourneyHubCard }) {
  const styles = colorStyles[journey.colorClass]

  return (
    <Link
      to={`/journey/${journey.slug}`}
      className={`
        relative cursor-pointer overflow-hidden flex flex-col justify-end
        text-inherit no-underline will-change-[flex]
        transition-all duration-[600ms] [transition-timing-function:cubic-bezier(0.22,1,0.36,1)]
        min-h-[220px] lg:min-h-[280px]
        border border-white/[0.08] rounded-xl
        before:content-[''] before:absolute before:inset-0 before:opacity-[0.15]
        before:transition-opacity before:duration-[600ms] before:[transition-timing-function:cubic-bezier(0.22,1,0.36,1)]
        hover:before:opacity-[0.25]
        after:content-[''] after:absolute after:inset-0 after:opacity-0
        after:transition-opacity after:duration-[600ms] after:[transition-timing-function:cubic-bezier(0.22,1,0.36,1)]
        after:pointer-events-none
        hover:after:opacity-100
        hover:scale-[1.02] hover:shadow-[0_12px_40px_rgba(0,0,0,0.3)]
        group
        ${styles.beforeBg}
        ${styles.afterBg}
      `}
    >
      {/* Content */}
      <div
        className="
          relative z-[2] px-6 py-8
          bg-gradient-to-t from-[rgba(10,13,18,0.95)] via-[rgba(10,13,18,0.7)] to-transparent
          transition-all duration-[600ms] [transition-timing-function:cubic-bezier(0.22,1,0.36,1)]
          group-hover:pb-10
        "
      >
        {/* Icon */}
        <div
          className={`
            w-[48px] h-[48px] rounded-[12px] flex items-center justify-center mb-4
            shadow-[0_4px_12px_rgba(0,0,0,0.2)]
            transition-all duration-500 [transition-timing-function:cubic-bezier(0.22,1,0.36,1)]
            group-hover:scale-[1.08] group-hover:-translate-y-0.5
            group-hover:shadow-[0_8px_20px_rgba(0,0,0,0.3)]
            ${styles.iconBg}
          `}
        >
          <JourneyIcon icon={journey.icon} />
        </div>

        {/* Title */}
        <h3
          className="
            font-serif text-xl font-semibold mb-2
            transition-all duration-500 [transition-timing-function:cubic-bezier(0.22,1,0.36,1)]
            group-hover:-translate-y-0.5
          "
        >
          {journey.title}
        </h3>

        {/* Tagline */}
        <p
          className="
            text-sm text-[#9ca3af] mb-3
            transition-all duration-500 [transition-timing-function:cubic-bezier(0.22,1,0.36,1)]
            group-hover:text-[#b8bcc4]
          "
        >
          {journey.tagline}
        </p>

        {/* Description */}
        <p
          className="
            text-sm text-[#7d8490] leading-relaxed
            opacity-100 max-h-[100px] translate-y-0
            lg:opacity-0 lg:max-h-0 lg:overflow-hidden lg:translate-y-2
            transition-all duration-500 [transition-timing-function:cubic-bezier(0.22,1,0.36,1)]
            lg:group-hover:opacity-100 lg:group-hover:max-h-[100px] lg:group-hover:translate-y-0
          "
        >
          {journey.description}
        </p>

        {/* CTA */}
        <div
          className={`
            inline-flex items-center gap-2 mt-3 text-sm font-semibold
            opacity-100 translate-y-0
            lg:opacity-0 lg:translate-y-2
            transition-all duration-500 [transition-timing-function:cubic-bezier(0.22,1,0.36,1)] delay-[50ms]
            lg:group-hover:opacity-100 lg:group-hover:translate-y-0
            ${styles.ctaColor}
          `}
        >
          See the journey
          <svg
            className="w-4 h-4 transition-transform duration-300 [transition-timing-function:cubic-bezier(0.22,1,0.36,1)] group-hover:translate-x-1"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M5 12h14M12 5l7 7-7 7" />
          </svg>
        </div>
      </div>

      {/* Accent Line */}
      <div
        className={`
          absolute bottom-0 left-0 right-0 h-[3px] origin-left
          scale-x-0 transition-transform duration-[600ms] [transition-timing-function:cubic-bezier(0.22,1,0.36,1)]
          group-hover:scale-x-100
          ${styles.accentLine}
        `}
      />
    </Link>
  )
}

export default function JourneyHubPage() {
  return (
    <>
      <SEO
        title="Adviser Practice Transformation Journeys | WealthSpace"
        description="Explore how WealthSpace transforms your practice. From meeting capture to business growth - discover the adviser workflow journeys that matter to you."
        keywords="adviser workflow, practice transformation, financial adviser efficiency, meeting capture, business growth, IFA productivity, adviser automation"
        url="/journeys"
      />

      {/* Hero */}
      <section className="pt-[100px] pb-16 px-6 max-w-[1200px] mx-auto relative z-[1] text-center">
        <p className="text-[14px] font-semibold text-accent-teal tracking-[0.1em] uppercase mb-6 opacity-0 animate-fade-in-up [animation-delay:0.1s]">
          Choose your story
        </p>
        <h1 className="font-serif text-[clamp(42px,6vw,60px)] font-normal leading-[1.15] tracking-[-0.02em] mb-6 max-w-[900px] mx-auto opacity-0 animate-fade-in-up [animation-delay:0.2s]">
          See how WealthSpace fits <span className="text-accent-teal">your</span> practice
        </h1>
        <p className="text-[19px] text-brand-muted leading-[1.7] font-normal max-w-[600px] mx-auto mb-[40px] opacity-0 animate-fade-in-up [animation-delay:0.3s]">
          Every adviser's journey is different. Click a story below to see how WealthSpace
          transforms the moments that matter most to you.
        </p>
      </section>

      {/* Journey Grid */}
      <section className="px-6 pb-[80px] relative z-[1]">
        <div className="max-w-[1200px] mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {journeyHubCards.map((journey) => (
            <JourneyCardComponent key={journey.slug} journey={journey} />
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-[100px] px-6 max-w-[900px] mx-auto text-center relative z-[1]">
        <div className="bg-[linear-gradient(145deg,rgba(255,255,255,0.03)_0%,rgba(255,255,255,0.01)_100%)] border border-white/[0.08] rounded-3xl py-20 px-12">
          <h2 className="font-serif text-[44px] font-normal text-brand-light tracking-[-0.02em] mb-5">
            Ready to start your journey?
          </h2>
          <p className="text-lg text-brand-muted leading-[1.7] max-w-[500px] mx-auto mb-10">
            Book a 30-minute demo and we'll show you how WealthSpace fits your practice.
          </p>
          <Button href="/get-started" size="lg">
            Get Started
            <svg
              className="w-[18px] h-[18px]"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </Button>
          <p className="text-sm text-brand-subtle mt-6">Free. Friendly. Absolutely no obligation.</p>
        </div>
      </section>
    </>
  )
}
